/**
 * phase-fix-compiler-preview-tab-download — THE inline document view path.
 *
 * Every surface that VIEWS a document file in-pane goes through this hook:
 * authed fetch via `v2.documents.viewFile` (?disposition=inline) → typed
 * PDF blob → object URL (revoked on change/unmount). Introduced by
 * de22d2d for the Submission Details preview pane; extracted here after
 * the Compiler's center Preview tab and the Inspector's Preview chip were
 * found still iframing the raw file endpoint — whose attachment
 * disposition makes browsers DOWNLOAD instead of render (Sharon's
 * repeatedly-downloading cover letter). A pane that iframes a blob: URL
 * renders inline regardless of endpoint disposition or storage-driver
 * SAS redirects, and carries Authorization.
 *
 * Explicit Download buttons are the opposite contract (attachment) and
 * must NOT use this hook.
 */
import { useEffect, useRef, useState } from 'react';
import { v2, ApiError } from '@/api/v2Client';

export interface InlineDocumentView {
  /** blob: URL to iframe, or null while loading / on error / when disabled. */
  readonly url: string | null;
  /** True while the authed fetch is in flight. */
  readonly loading: boolean;
  /** Honest failure text, or null. */
  readonly error: string | null;
  /**
   * Page count parsed from the PDF bytes so the in-document page control can
   * show an honest "x of y". `null` means UNKNOWN (non-PDF, unparseable, or an
   * environment without DecompressionStream) — the caller then shows plain
   * arrows WITHOUT a total (item 2), rather than hiding. A known `1` means a
   * confirmed single-page document → the caller hides the control.
   */
  readonly pageCount: number | null;
}

/** Count the page markers in a chunk of decoded PDF text (`/Type /Page`, then
 * the page-tree `/Count N` fallback). Returns null when neither is present. */
function countMarkers(text: string): number | null {
  const pageObjs = text.match(/\/Type\s*\/Page(?![sA-Za-z])/g);
  if (pageObjs && pageObjs.length > 0) return pageObjs.length;
  const counts = [...text.matchAll(/\/Count\s+(\d+)/g)].map((m) => Number(m[1]));
  const maxCount = counts.length > 0 ? Math.max(...counts) : 0;
  return maxCount > 0 ? maxCount : null;
}

/** Inflate a zlib/deflate byte range via the platform DecompressionStream.
 * Tries zlib-wrapped (`deflate`, what PDF FlateDecode uses) then raw. Drives
 * the stream's writer/reader directly rather than via Blob.stream() — jsdom
 * (the test env) provides DecompressionStream but not Blob.prototype.stream. */
async function inflate(bytes: Uint8Array): Promise<Uint8Array | null> {
  const DS = globalThis.DecompressionStream;
  if (typeof DS === 'undefined') return null;
  for (const fmt of ['deflate', 'deflate-raw'] as const) {
    try {
      const ds = new DS(fmt);
      const writer = ds.writable.getWriter();
      // The reader below is what surfaces a bad-data error (→ caught, next
      // format tried); swallow the writer-side rejection so it never becomes an
      // unhandled promise rejection when the format doesn't match.
      // `.slice()` gives an ArrayBuffer-backed copy — a `subarray` view is typed
      // `Uint8Array<ArrayBufferLike>` which is not a `BufferSource` under TS 5.9+.
      writer.write(bytes.slice()).catch(() => undefined);
      writer.close().catch(() => undefined);
      const reader = ds.readable.getReader();
      const chunks: Uint8Array[] = [];
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value) chunks.push(value as Uint8Array);
      }
      const total = chunks.reduce((n, c) => n + c.length, 0);
      const out = new Uint8Array(total);
      let offset = 0;
      for (const c of chunks) {
        out.set(c, offset);
        offset += c.length;
      }
      return out;
    } catch {
      // try the next format
    }
  }
  return null;
}

/**
 * phase-forms-and-documents-review-batch (item 2) — robust page count from raw
 * PDF bytes, no PDF library.
 *
 * DIAGNOSIS of why the seeded 5-page cover letter counted as unknown: the seed
 * fabricator writes PDFs with `pdf-lib`, whose default `save()` packs every
 * object into FlateDecode **object streams** behind an **XRef stream**. So the
 * `/Type /Page` objects AND the page-tree `/Count` live inside compressed bytes
 * — invisible to a latin1 text scan. The old parser saw neither and returned
 * null, hiding the control while the native viewer (which decompresses) showed
 * "5 pages".
 *
 * Fix: try the raw text first (classic PDFs); if that finds nothing, INFLATE
 * every `stream…endstream` block and count the markers in the decompressed
 * bytes — that reaches the `/Type /Page` objects and the `/Count` fallback the
 * prompt requires. Returns null only when the count is genuinely unknowable
 * (no DecompressionStream, or nothing found even after inflation) — the caller
 * then shows plain arrows without an "x of y".
 */
export async function countPdfPages(buf: ArrayBuffer): Promise<number | null> {
  const bytes = new Uint8Array(buf);
  // Latin1 keeps byte values 1:1 so string offsets are byte offsets.
  const latin1 = new TextDecoder('latin1').decode(bytes);

  const raw = countMarkers(latin1);
  if (raw !== null) return raw;

  if (typeof globalThis.DecompressionStream === 'undefined') return null;

  let corpus = '';
  const streamStart = /stream\r?\n/g;
  let m: RegExpExecArray | null;
  while ((m = streamStart.exec(latin1)) !== null) {
    const start = m.index + m[0].length;
    const end = latin1.indexOf('endstream', start);
    if (end < 0) continue;
    // Trim the EOL that precedes `endstream`.
    let e = end;
    if (latin1[e - 1] === '\n') e -= 1;
    if (latin1[e - 1] === '\r') e -= 1;
    if (e <= start) continue;
    const out = await inflate(bytes.subarray(start, e));
    if (out) corpus += new TextDecoder('latin1').decode(out);
  }
  return corpus ? countMarkers(corpus) : null;
}

export interface InlineDocumentViewOptions {
  readonly enabled?: boolean;
  /**
   * navigation-completion — an OPTIONAL authed-response loader for an EXACT
   * artifact that is not the document's default inline file (e.g. a specific
   * navigation-draft revision via `GET …/navigation/file?revision=N`). When
   * supplied it is used INSTEAD of `v2.documents.viewFile`, carrying its own
   * Authorization (it must resolve to a `Response` whose body is the exact PDF
   * bytes). A failure surfaces as `error` and leaves `url` null — the caller
   * never silently falls back to the original document's bytes.
   *
   * The loader's identity is read through a ref, so passing a fresh closure each
   * render does NOT re-trigger the fetch; reloads are driven solely by
   * {@link cacheKey} (and `documentId`/`enabled`).
   */
  readonly loader?: () => Promise<Response>;
  /**
   * When a {@link loader} is used, the value that identifies the exact artifact
   * (e.g. `nav:<documentId>:rev<N>`). The fetch re-runs whenever this changes,
   * so a new revision reloads the preview. Ignored when no loader is supplied.
   */
  readonly cacheKey?: string;
}

export function useInlineDocumentView(
  documentId: string | null,
  opts: InlineDocumentViewOptions = {},
): InlineDocumentView {
  const hasLoader = typeof opts.loader === 'function';
  const enabled = opts.enabled !== false && (hasLoader || documentId !== null);

  // navigation-completion preview-identity fix — state is tagged with the
  // identity of the request that produced it. Effects run AFTER a render
  // commits, so a documentId/cacheKey/enabled change would otherwise expose
  // the previous artifact's url/pageCount/error for one frame under the new
  // identity (a new revision caption over stale bytes). The render-time guard
  // below suppresses any state whose identity no longer matches.
  const requestIdentity = enabled
    ? hasLoader
      ? `loader|${documentId ?? ''}|${opts.cacheKey ?? ''}`
      : `doc|${documentId ?? ''}`
    : null;
  interface ViewState {
    readonly identity: string | null;
    readonly url: string | null;
    readonly loading: boolean;
    readonly error: string | null;
    readonly pageCount: number | null;
  }
  const [state, setState] = useState<ViewState>({
    identity: null,
    url: null,
    loading: false,
    error: null,
    pageCount: null,
  });

  // Read the loader through a ref so a new closure per render doesn't re-fetch;
  // reloads are driven by documentId / enabled / cacheKey only.
  const loaderRef = useRef(opts.loader);
  loaderRef.current = opts.loader;
  const cacheKey = opts.cacheKey;

  useEffect(() => {
    if (!enabled || (!hasLoader && !documentId)) {
      setState({ identity: null, url: null, loading: false, error: null, pageCount: null });
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    const identity = requestIdentity;
    setState({ identity, url: null, loading: true, error: null, pageCount: null });
    const load = loaderRef.current
      ? loaderRef.current()
      : v2.documents.viewFile(documentId as string);
    load
      .then(async (res) => {
        const bytes = await res.arrayBuffer();
        if (cancelled) return;
        // phase-package-viewer-live-structure addendum 3 — label the blob with
        // the REAL filename. An anonymous Blob's object URL is a bare UUID, so
        // the embedded viewer's toolbar titled the document "0e04aba0-…" and
        // its print/download produced UUID-named files. The server already
        // sends `Content-Disposition: inline; filename="<real>"` on this
        // response (documents-file.ts); read it back and back the object URL
        // with a named File — the browser then uses that name for the
        // viewer-initiated download/print artifacts. This is the client-side
        // equivalent of the disposition header for a blob: URL.
        const disposition = res.headers?.get?.('content-disposition') ?? '';
        const nameMatch = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
        const realName = nameMatch?.[1] ? decodeURIComponent(nameMatch[1]) : 'document.pdf';
        objectUrl = globalThis.URL.createObjectURL(
          new globalThis.File([bytes], realName, { type: 'application/pdf' }),
        );
        // Resolve the (best-effort) page count BEFORE revealing the preview so
        // the page control settles to its final state in one paint — no flash
        // of arrows-without-count that then resolves to a known total or hides.
        // The bytes are already in hand; counting a preview-sized PDF is cheap.
        const count = await countPdfPages(bytes).catch(() => null);
        if (cancelled) return;
        setState({ identity, url: objectUrl, loading: false, error: null, pageCount: count });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          identity,
          url: null,
          loading: false,
          error:
            err instanceof ApiError ? err.message : 'Could not load the document for viewing.',
          pageCount: null,
        });
      });
    return () => {
      cancelled = true;
      if (objectUrl) globalThis.URL.revokeObjectURL(objectUrl);
    };
    // `cacheKey` reloads the loader path on a new artifact revision; `documentId`
    // drives the default viewFile path. The loader itself is read via ref.
    // `requestIdentity` is derived from those same inputs; listing it keeps
    // exhaustive-deps honest without changing when the effect fires.
  }, [documentId, enabled, hasLoader, cacheKey, requestIdentity]);

  // Stale-identity suppression: never expose an artifact under a different
  // request identity. While a new fetch is awaited the hook honestly reports
  // loading (or empty when disabled), synchronously, before the effect clears.
  if (state.identity !== requestIdentity) {
    return { url: null, loading: requestIdentity !== null, error: null, pageCount: null };
  }
  return { url: state.url, loading: state.loading, error: state.error, pageCount: state.pageCount };
}
